import { IUrlData, IProduct, IAction, ISide, IHolder, IDimensions } from "../types"
import { getUrlData } from "../state"

const colors = {
  "Black": `rgb(69, 73, 67)`,
  "Brown": `rgb(93, 70, 19)`,
  "Blue": `rgb(96, 120, 169)`,
  "Red": `rgb(215, 86, 68)`,
  "Green": `rgb(56, 87, 36)`,
  "Grey": `rgb(118, 113, 113)`,
  "Yellow": `rgb(197, 139, 82)`,
}

export const parseUrlState = (b64: string): IUrlData => {
  try {
    const data: IUrlData = JSON.parse(atob(b64))
    const options = data.variant.title.split("/")
    const colorName = options[1].trim()
    data.color = { name: colorName, value: colors[colorName] }
    data.embossed = options[2].trim() === "Deboss"
    data.dimensions = (d => ({ width: +d[0], height: +d[1] }))(options[0].trim().match(/\d+/g))
    return data
  } catch (err) {
    console.error(err)
  }
  return undefined
}

export const loadRenderConfig = async (urlData: IUrlData) => {
  let config: { load: ConfigLoad }

  const id = urlData.product.id
  if (id === 4548751982723)
    config = await import("./basic")

  else if (id === 4564974600323)
    config = await import("./class")

  else if (id === 4549765595267 || id === 4549337448579)
    config = await import("./classic")

  else if (id === 4549256839299 || id === 4549273616515)
    config = await import("./pocket_top")

  else if (id === 4944935157891)
    config = await import("./style")

  else if (id === 4549296554115)
    config = await import("./pocket_left_hard")

  else if (id === 4549310414979)
    config = await import("./pocket_left_soft")

  return config.load(urlData)
}

export const loadProductConfig = (urlData: IUrlData) =>
  createProduct(urlData.product.title, urlData.dimensions)

interface ConfigLoadAssets {
  additionBlend?: string
  mask?: HTMLImageElement,
  texture?: HTMLImageElement,
  textureAlpha?: number,
  addition?: HTMLImageElement
  additionAlpha?: number,
}

interface ConfigLoadSide {
  assets: ConfigLoadAssets
}

export type ConfigLoad = (urlData: IUrlData) => Promise<{
  front: ConfigLoadSide,
  back: ConfigLoadSide,
  urlData?: Partial<IUrlData>
}>

const getDefaultSnapPoints = (width: number, height: number) => {
  const sp0 = Math.min(width, height) * .1
  return [
    { snap: 0, x: sp0, }, { snap: .5, x: width * .5, }, { snap: 1, x: width - sp0, },
    { snap: 0, y: sp0, }, { snap: .5, y: height * .5, }, { snap: 1, y: height - sp0, },
  ]
}

export const createProduct = (title: string, { width, height }: IDimensions) => {
  const productKey = `${title.toLowerCase().replace(/\s/g, "_")}_${width}x${height}`
  const product: IProduct = {
    key: productKey,
    title: title,
    sideIndex: 0,
    sides: []
  }

  product.sides.push({
    product,
    key: "front",
    title: "front",
    position: { width, height },
    margin: { x: 0, y: 0 },
    holderIndex: undefined,
    holders: [],
    renderer: undefined,
    snapPoints: getDefaultSnapPoints(width, height)
  })

  product.sides.push({
    product,
    key: "back",
    title: "back",
    position: { width, height },
    margin: { x: 0, y: 0 },
    holderIndex: undefined,
    holders: [],
    renderer: undefined,
    snapPoints: getDefaultSnapPoints(width, height)
  })

  return product
}

export const getTextHolderActions = (): IAction[] => [
  {
    key: "text",
    title: "Text",
    icon: "short_text",
    type: "text",
    value: "",
    state: {
    },
  },
  {
    key: "font",
    title: "Font",
    icon: "text_format",
    type: "select",
    view: "select",
    options:
      [
        ...["Anton", "Bebas Neue", "IBM Plex Mono", "Noticia Text", "Roboto", "Roboto Condensed", "Roboto Mono", "Roboto Slab", "Russo One", "Varela Round", "Baloo Bhaijaan", "Great Vibes", "Indie Flower", "Kaushan Script", "Shadows Into Light Two"]
          .map(value => ({ title: `<span style="font-family: ${value}">${value}</span>`, value, state: { arabic: false } })),
        ...["Almarai", "Amiri", "Cairo", "El Messiri", "Lalezar"]
          .map(value => ({ title: `<span style="font-family: ${value}">${value} (نصك)</span>`, value, state: { arabic: true, hidden: true } })),
      ],
    value: "Anton",
  },
  {
    key: "color",
    title: "Color",
    icon: "color_lens",
    type: "color",
    value: getUrlData().color.name === "Black" ? "rgb(255,255,255)" : "rgb(0,0,0)",
    options:
      ["rgb(0,0,0)", "rgb(158, 158, 158)", "rgb(255,255,255)", "rgb(244, 67, 54)", "rgb(233, 30, 99)", "rgb(156, 39, 176)", "rgb(103, 58, 183)", "rgb(63, 81, 181)", "rgb(33, 150, 243)", "rgb(3, 169, 244)", "rgb(0, 188, 212)", "rgb(0, 150, 136)", "rgb(76, 175, 80)", "rgb(139, 195, 74)", "rgb(205, 220, 57)", "rgb(255, 235, 59)", "rgb(255, 193, 7)", "rgb(255, 152, 0)", "rgb(255, 87, 34)", "rgb(121, 85, 72)"]
        .map(value => ({
          title: `<div style="background-color: ${value}"></div>`,
          value
        })),
  },
  {
    key: "style",
    title: "Style",
    icon: "format_italic",
    type: "checkbox",
    options:
      ["bold", "italic"]
        .map(value => ({
          title: `<span>${value}</span>`,
          value,
          icon: `format_${value}`
        })),
    value: { bold: false, italic: false }
  },
  {
    key: "align",
    title: "Align horizontal",
    icon: "format_align_center",
    type: "select",
    view: "grid",
    options:
      ["left", "center", "right"]
        .map(value => ({
          title: `<span>${value}</span>`,
          value,
          icon: `format_align_${value}`
        })),
    value: "center"
  },
  {
    key: "justify",
    title: "Align vertical",
    icon: "vertical_align_center",
    type: "select",
    view: "grid",
    options:
      ["up", "center", "down"]
        .map(value => ({
          title: `<span>${value}</span>`,
          value: value === "up" ? "down" : value === "down" ? "up" : value,
          icon: `vertical_align_${value === "up" ? "top" : value === "down" ? "bottom" : value}`
        })),
    value: "center"
  },
  {
    key: "size",
    title: "Size",
    icon: "format_size",
    type: "number",
    value: 20
  },
  {
    key: "depth",
    title: "Depth",
    icon: "filter_1",
    type: "select",
    view: "grid",
    options:
      ["back", "front"]
        .map(value => ({
          title: value,
          value,
          icon: `arrow_drop_${value === "back" ? "down" : "up"}`
        })),
    value: ""
  }
]

export const getImageHolderActions = (): IAction[] => [
  {
    key: "image",
    title: "Image",
    icon: "image",
    type: "image",
    value: ""
  },
  {
    key: "fit",
    title: "Fit",
    icon: "center_focus_strong",
    type: "select",
    options:
      ["contain", "cover"] //, "stretch"
        .map(value => ({
          title: value,
          value,
          icon: value === "contain" ? "fullscreen_exit" : "fullscreen"
        })),
    value: "contain"
  },
  {
    key: "depth",
    title: "Depth",
    icon: "filter_1",
    type: "select",
    options:
      ["back", "front"]
        .map(value => ({
          title: value,
          value,
          icon: `arrow_drop_${value === "back" ? "down" : "up"}`
        })),
    value: ""
  }
]

export const createHolder = (type: "text" | "image", side: ISide) => {
  const actions = type === "text" ? getTextHolderActions() : getImageHolderActions()
  const size = Math.min(side.position.width, side.position.height)
  const position = {
    x: size * .2,
    y: size * .2,
    width: size * .6,
    height: size * .6,
    rotation: 0
  }

  const holder: IHolder = {
    type,
    side,
    position,
    actions,
    actionIndex: 0,
    buffer: undefined,
    dirty: true,
    cache: {}
  }

  return holder
}